/**
 * Iframe Message Handler for Sol Frontend
 * Handles postMessage communication when Sol runs inside an iframe
 * Receives authentication tokens and case data from parent window
 */

import { useState, useEffect, useRef } from 'react';

// Origin checking has been disabled to allow all parent windows to embed Sol
// Previously used REACT_APP_EMBED_ALLOWED_ORIGINS environment variable

/**
 * Validates the origin of incoming messages (based on mira-pin-frontend implementation)
 * @param {string} origin - The origin of the message
 * @returns {boolean} - Whether the origin is allowed
 */
const isOriginAllowed = (origin) => {
  // Origin checks disabled - allow all origins
  return true;
};

/**
 * Validates the structure of Sol UI API token payload
 * @param {Object} payload - The payload to validate
 * @returns {Object} - Validation result with isValid boolean and errors array
 */
const validateSolApiTokenPayload = (payload) => {
  const errors = [];  
  if (!payload) {
    errors.push('Payload is missing');
    return { isValid: false, errors };
  }  
  if (!payload.api_token) {
    errors.push('api_token is required');
  }  
  if (!payload.mirasol_case_id) {
    errors.push('mirasol_case_id is required');
  }  
  // Optional validation for provider info
  if (payload.provider_info) {
    if (!payload.provider_info.id) {
      errors.push('provider_info.id is required when provider_info is provided');
    }
  }  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Creates the iframe message handler
 * @param {Object} callbacks - Callback functions for different message types
 * @param {Function} callbacks.onProviderAuth - Called when provider authentication data is received
 * @param {Function} callbacks.onError - Called when an error occurs
 * @param {Function} callbacks.onCaseData - Called when case data is received
 * @returns {Function} - The message handler function
 */
export const createIframeMessageHandler = (callbacks = {}) => {
  const {
    onProviderAuth,
    onError,
    onCaseData
  } = callbacks;

  return (event) => {
    // Origin validation disabled - accepting messages from all origins
    
    const { type, payload } = event.data;
    
    // console.log('Received iframe message:', { type, payload, origin: event.origin });

    switch (type) {
      case 'MIRASOL_SOL_UI_API_TOKEN': {
        // Validate the payload structure
        const validation = validateSolApiTokenPayload(payload);
        
        if (!validation.isValid) {
          if (onError) {
            onError({
              type: 'INVALID_PAYLOAD',
              message: 'Invalid provider authentication payload',
              errors: validation.errors
            });
          }
          return;
        }
        if (onProviderAuth) {
          onProviderAuth(payload);
        }
        break;
      }

      case 'CASE_DATA': {
        // Handle case-specific data updates
        if (payload && payload.case_id) {
          if (onCaseData) {
            onCaseData(payload);
          }
        } else {
        }
        break;
      }

      case 'IFRAME_READY': {
        // Send confirmation that Sol is ready to receive data
        sendMessageToParent('SOL_READY', {
          timestamp: new Date().toISOString(),
          version: process.env.REACT_APP_VERSION || '1.0.0'
        });
        break;
      }

      default:
        // console.log('Unhandled message type:', type);
        break;
    }
  };
};

/**
 * Sends a message to the parent window
 * @param {string} type - Message type
 * @param {Object} payload - Message payload
 */
export const sendMessageToParent = (type, payload = {}) => {
  if (window.parent && window.parent !== window) {
    const message = {
      type,
      payload,
      timestamp: new Date().toISOString(),
      source: 'sol-frontend'
    };
    
    // console.log('Sending message to parent:', message);
    // Get target origin from environment
    const targetOrigin = process.env.REACT_APP_PARENT_WINDOW_ORIGIN || '';
    window.parent.postMessage(message, targetOrigin);
  } else {
    // console.warn('No parent window available for messaging');
  }
};

/**
 * Detects if the application is running inside an iframe
 * @returns {boolean} - Whether the app is in an iframe
 */
export const isInIframe = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    // In case of cross-origin restrictions
    return true;
  }
};

/**
 * Sets up iframe communication and returns cleanup function
 * @param {Object} callbacks - Callback functions for message handling
 * @returns {Function} - Cleanup function to remove event listeners
 */
export const setupIframeMessaging = (callbacks) => {
  // console.log('Setting up iframe messaging...');
  
  const messageHandler = createIframeMessageHandler(callbacks);
  
  // Add event listener - always listen for messages
  window.addEventListener('message', messageHandler);
  
  // Notify parent that Sol is ready (if in iframe)
  if (isInIframe()) {
    sendMessageToParent('SOL_READY', {
      timestamp: new Date().toISOString(),
      ready: true
    });
  }
  
  // Return cleanup function
  return () => {
    window.removeEventListener('message', messageHandler);
    // console.log('Iframe messaging cleaned up');
  };
};

/**
 * Hook for React components to use iframe messaging
 * @param {Object} callbacks - Callback functions
 * @returns {Object} - Utilities for iframe communication
 */
export const useIframeMessaging = (callbacks) => {
  const [isIframeMode, setIsIframeMode] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const callbacksRef = useRef(callbacks);
  const hasSetup = useRef(false);
  
  // Update ref when callbacks change
  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);
  
  useEffect(() => {
    // Only setup once to prevent multiple SOL_READY messages
    if (hasSetup.current) {
      return;
    }
    hasSetup.current = true;
    
    const inIframe = isInIframe();
    setIsIframeMode(inIframe);
    setConnectionStatus('connecting');
    
    // console.log('Setting up iframe messaging...', { inIframe });
    
    const cleanup = setupIframeMessaging({
      onProviderAuth: (payload) => {
        setConnectionStatus('authenticated');
        if (callbacksRef.current.onProviderAuth) {
          callbacksRef.current.onProviderAuth(payload);
        }
      },
      onCaseData: (payload) => {
        if (callbacksRef.current.onCaseData) {
          callbacksRef.current.onCaseData(payload);
        }
      },
      onError: (error) => {
        setConnectionStatus('error');
        if (callbacksRef.current.onError) {
          callbacksRef.current.onError(error);
        }
      }
    });
    
    return cleanup;
  }, []); // Empty array is correct now - callbacks accessed via ref
  
  return {
    isIframeMode,
    connectionStatus,
    sendMessage: sendMessageToParent
  };
};